import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { playSound } from '../lib/soundEffects';

const {
  FiExternalLink, FiTrash2, FiFolder, FiX, FiCheckSquare, FiDownload,
} = FiIcons;

export default function BatchActionBar({
  selectedItems = [],
  onClear,
  onClearSelection,
  onOpenAll,
  onLaunchAll,
  onDeleteSelected,
  onDelete,
  onExportSelected,
  onExportJSON,
  onMoveSelected,
  themeColors: tc,
}) {
  if (!selectedItems.length) return null;

  const handleClear = onClear || onClearSelection;
  const handleLaunch = onLaunchAll || onOpenAll;
  const handleDelete = onDeleteSelected || onDelete;
  const handleExport = onExportJSON || onExportSelected;
  const hasLinks = selectedItems.some((i) => i.type === 'link' || !!i.url);

  return (
    <AnimatePresence>
      <div className="fixed bottom-4 sm:bottom-20 left-0 right-0 z-50 flex justify-center pointer-events-none px-3">
        <motion.div
          initial={{ y: 50, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 50, opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.16, ease: 'easeOut' }}
          className="pointer-events-auto w-auto max-w-[calc(100vw-1.5rem)] sm:max-w-xl overflow-x-auto flex items-center gap-1.5 sm:gap-3 px-2.5 sm:px-4 py-1.5 sm:py-2.5 rounded-2xl bg-[#14141a]/95 border border-[#2d2d38] shadow-2xl backdrop-blur-xl text-white text-xs whitespace-nowrap scrollbar-none"
          style={{
            boxShadow: '0 16px 40px -10px rgba(0,0,0,0.85), 0 0 25px rgba(16, 185, 129, 0.12)',
          }}
        >
        <div className="flex items-center gap-1 sm:gap-2 pr-1.5 sm:pr-2 border-r border-[#2d2d38] flex-shrink-0">
          <SafeIcon icon={FiCheckSquare} className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-emerald-400 flex-shrink-0" />
          <span className="font-bold text-emerald-300 whitespace-nowrap text-xs">
            {selectedItems.length}
            <span className="hidden sm:inline font-semibold"> selected</span>
          </span>
        </div>

        {/* Batch Move to Folder */}
        {onMoveSelected && (
          <button
            type="button"
            onClick={() => { playSound('click'); onMoveSelected(selectedItems); }}
            className="flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30 transition-all font-medium cursor-pointer flex-shrink-0"
            title="Move all selected items to another folder across app"
          >
            <SafeIcon icon={FiFolder} className="w-3.5 h-3.5 text-emerald-400" />
            <span>
              <span className="sm:hidden">Move</span>
              <span className="hidden sm:inline">Move to...</span>
            </span>
          </button>
        )}

        {handleLaunch && hasLinks && (
          <button
            type="button"
            onClick={() => { playSound('launch'); handleLaunch(selectedItems); }}
            className="flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl bg-[#202028] hover:bg-[#2b2b36] text-neutral-200 transition-colors cursor-pointer flex-shrink-0"
            title="Open all selected links in browser tabs"
          >
            <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5 text-cyan-400" />
            <span>
              <span className="sm:hidden">Launch</span>
              <span className="hidden sm:inline">Launch All</span>
            </span>
          </button>
        )}

        {handleExport && (
          <button
            type="button"
            onClick={() => { playSound('click'); handleExport(selectedItems); }}
            className="flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl bg-[#202028] hover:bg-[#2b2b36] text-neutral-200 transition-colors cursor-pointer flex-shrink-0"
            title="Export selected items as JSON"
          >
            <SafeIcon icon={FiDownload} className="w-3.5 h-3.5 text-amber-400" />
            <span>Export</span>
          </button>
        )}

        {handleDelete && (
          <button
            type="button"
            onClick={() => { playSound('delete'); handleDelete(selectedItems); }}
            className="flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border border-rose-500/25 transition-colors cursor-pointer flex-shrink-0"
            title="Delete all selected items"
          >
            <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5 text-rose-400" />
            <span>Delete</span>
          </button>
        )}

        {handleClear && (
          <button
            type="button"
            onClick={() => { playSound('click'); handleClear(); }}
            className="p-1 rounded-lg text-neutral-400 hover:text-white hover:bg-white/10 transition-colors ml-0.5 sm:ml-1 cursor-pointer flex-shrink-0"
            title="Clear selection"
          >
            <SafeIcon icon={FiX} className="w-4 h-4" />
          </button>
        )}
      </motion.div>
    </div>
  </AnimatePresence>
  );
}
